import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { StateChip } from '@/features/execution/shared'
import { RegisterAlerts } from '@/features/registers/RegistersPanel'
import { PeopleAlerts, usePeopleAlertCount } from '@/features/people/PeopleWidgets'
import type { RegisterAlert } from '@/features/registers/types'
import type { InboxRow } from './types'
import { gateLabel, itemLabel, roleLabel, useWfT } from './shared'

/**
 * Each person's open work (docs/project-workflow-design.md): projects whose next gate has an item
 * they own, or that they sign. Gates ready to sign come first. A CEO also sees projects with no
 * Project Manager, since nobody else can move them.
 */
export function MyWorkPage() {
  const t = useWfT()
  const { data, isPending } = useQuery<InboxRow[]>({ queryKey: ['workflow-inbox'], queryFn: () => api.get<InboxRow[]>('/api/workflow/inbox') })
  const { data: alerts } = useQuery<RegisterAlert[]>({ queryKey: ['register-alerts'], queryFn: () => api.get<RegisterAlert[]>('/api/registers/alerts') })
  const peopleAlerts = usePeopleAlertCount()
  const loader = usePageLoader(isPending, t('loading'))
  if (loader) return loader
  if (!data) return null

  const ready = data.filter((r) => r.canSign && r.open === 0)
  const rest = data.filter((r) => !(r.canSign && r.open === 0) && (r.myItems.length > 0 || r.canSign))
  const noPm = data.filter((r) => r.noProjectManager && r.myItems.length === 0 && !r.canSign)

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">{t('myWork')}</h1>
        <p className="mt-1 max-w-3xl text-sm text-neutral-500">{t('myWorkSubtitle')}</p>
      </header>
      <PeopleAlerts />
      <RegisterAlerts />
      {data.length === 0 && (alerts?.length ?? 0) === 0 && peopleAlerts === 0 && <p className="rounded-2xl bg-white p-6 text-sm text-neutral-500 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">{t('nothing')}</p>}
      {ready.length > 0 && <Group title={`${t('readyToSign')} (${ready.length})`} rows={ready} />}
      {rest.length > 0 && <Group title={`${t('colMine')} (${rest.length})`} rows={rest} />}
      {noPm.length > 0 && <Group title={`${t('needsPm')} (${noPm.length})`} rows={noPm} compact />}
    </div>
  )
}

function Group({ title, rows, compact = false }: { title: string; rows: InboxRow[]; compact?: boolean }) {
  const t = useWfT()
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{title}</h2>
      <ul className="divide-y divide-neutral-100 overflow-hidden rounded-2xl bg-white ring-1 ring-neutral-200 dark:divide-neutral-800 dark:bg-neutral-900 dark:ring-neutral-800">
        {rows.map((r) => (
          <li key={r.projectId}>
            <Link to={`/projects/${r.projectId}`} className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
              <div className="min-w-0 flex-1 basis-60">
                <div className="font-medium">{r.name ?? '—'}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                  {r.code && <span>{r.code}</span>}
                  <StateChip state={r.state} />
                  <span>→ {gateLabel(t, r.gate)}</span>
                  <span className="tabular">{t('openOf', { open: r.open, total: r.total })}</span>
                </div>
              </div>
              {!compact && (
                <div className="min-w-0 flex-1 basis-72 space-y-1 text-sm">
                  {r.myItems.map((i) => <div key={i} className="flex gap-2"><span aria-hidden className="mt-1.5 size-2 shrink-0 rounded-full ring-2 ring-neutral-400" />{itemLabel(t, r.gate, i)}</div>)}
                  {r.canSign && <div className="text-xs font-semibold text-brand-700 dark:text-brand-300">{t('youSign')} · {roleLabel(t, r.signer)}</div>}
                </div>
              )}
              {r.noProjectManager && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">{t('needsPm')}</span>}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
